import React, { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import StarRating from "../components/ui/StarRating";
import { useLanguage } from "../contexts/LanguageContext";
import logger from "../utils/logger.js";
import toastUtils from "../utils/toastUtils.js";
import { setupMessagePolling, stopMessagePolling } from "../utils/echo.js";
import {
  SERVICE_ORDER_ATTACHMENT_ACCEPT,
  validateServiceOrderAttachment,
} from "../utils/serviceOrderAttachments.js";
import "../styles/order-details.css";
import {
  approveServiceOrderMilestone,
  formatServiceCurrency,
  getFreelancerServiceTitle,
  getServiceOrderDetails,
  openServiceOrderDispute,
  listServiceOrderDisputes,
  submitServiceOrderReview,
  updateServiceOrderStatus,
  getOrderMessages,
  sendOrderMessage,
  findRecoveredOrderMessage,
  markOrderMessagesRead,
  requestOrderCancellation,
  acceptRejectDelivery,
} from "../api/freelancerServices";

function getStatusMeta(status, isAr) {
  const map = {
    pending: { className: "text-bg-warning", label: isAr ? "قيد الانتظار" : "Pending" },
    paid: { className: "text-bg-primary", label: isAr ? "مدفوع" : "Paid" },
    in_progress: { className: "text-bg-info", label: isAr ? "قيد التنفيذ" : "In Progress" },
    delivered: { className: "text-bg-success", label: isAr ? "تم التسليم" : "Delivered" },
    completed: { className: "text-bg-success", label: isAr ? "مكتمل" : "Completed" },
    cancelled: { className: "text-bg-danger", label: isAr ? "ملغي" : "Cancelled" },
    disputed: { className: "text-bg-danger", label: isAr ? "نزاع" : "Disputed" },
    revision_requested: { className: "text-bg-warning", label: isAr ? "طلب مراجعة" : "Revision Requested" },
    cancellation_requested: { className: "text-bg-warning", label: isAr ? "طلب إلغاء" : "Cancellation Requested" },
  };

  return map[String(status || "").toLowerCase()] || {
    className: "text-bg-secondary",
    label: status || (isAr ? "غير معروف" : "Unknown"),
  };
}

function createReviewDraft() {
  return {
    rating: "5",
    title: "",
    comment: "",
  };
}

function createDisputeDraft() {
  return {
    reason: "",
    details: "",
  };
}

function getStorageBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL || "/laravel-api/api";
  return apiUrl.replace(/\/api(\/v1)?$/, "");
}

export default function ServiceOrderDetails() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const { orderId: orderIdParam } = useParams();
  const orderId = orderIdParam ? Number(orderIdParam) : null;

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionInProgress, setActionInProgress] = useState(false);
  const [notice, setNotice] = useState("");
  const [reviewDraft, setReviewDraft] = useState(createReviewDraft());
  const [disputeDraft, setDisputeDraft] = useState(createDisputeDraft());
  const [orderMessages, setOrderMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [messageFile, setMessageFile] = useState(null);
  const [messageFileError, setMessageFileError] = useState("");
  const [cancellationReason, setCancellationReason] = useState("");
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  const messagesEndRef = useRef(null);
  const messageStreamRef = useRef(null);
  const previousMessageCountRef = useRef(0);

  const refreshNotifications = () => {
    try {
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch {
      window.dispatchEvent(new Event("mg:notifications-refresh"));
    }
  };

  useEffect(() => {
    let active = true;

    async function load() {
      if (!orderId) {
        if (active) setError(isAr ? "معرف الطلب غير صالح." : "Invalid order ID.");
        return;
      }

      try {
        setLoading(true);
        setError("");

        const orderDetails = await getServiceOrderDetails(orderId);
        if (active) {
          setOrder(orderDetails);

          // Load messages
          try {
            const messages = await getOrderMessages(orderId);
            setOrderMessages(messages);
            await markOrderMessagesRead(orderId);
            previousMessageCountRef.current = Array.isArray(messages) ? messages.length : 0;
          } catch (msgErr) {
            logger.error("Failed to load messages", msgErr);
          }

          // Setup polling for new messages
          try {
            setupMessagePolling(orderId, (polledMessages) => {
              if (active && polledMessages && Array.isArray(polledMessages)) {
                const newMessageCount = polledMessages.length;
                const previousCount = previousMessageCountRef.current;

                // Check if new messages arrived
                if (newMessageCount > previousCount) {
                  const newMessagesCount = newMessageCount - previousCount;
                  const lastMessage = polledMessages[newMessageCount - 1];
                  const lastSender = lastMessage?.sender?.name || (isAr ? "مستخدم" : "User");

                  // Show toast for new message(s)
                  if (newMessagesCount === 1) {
                    toastUtils.info(
                      isAr ? "رسالة جديدة" : "New Message",
                      isAr
                        ? `رسالة جديدة من ${lastSender}`
                        : `New message from ${lastSender}`,
                      { autoClose: 4000 }
                    );
                  } else {
                    toastUtils.info(
                      isAr ? "رسائل جديدة" : "New Messages",
                      isAr
                        ? `${newMessagesCount} رسائل جديدة`
                        : `${newMessagesCount} new messages`,
                      { autoClose: 4000 }
                    );
                  }
                }

                // Update state with all messages from polling
                setOrderMessages(polledMessages);
                previousMessageCountRef.current = newMessageCount;

                // Trigger navbar to refresh notifications badge
                refreshNotifications();
              }
            });
          } catch (err) {
            logger.error("Failed to setup message polling", err);
          }
        }
      } catch (err) {
        logger.error("Load service order error", err);
        if (active) {
          setError(err?.message || (isAr ? "تعذر تحميل تفاصيل الطلب." : "Unable to load order details."));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
      if (orderId) {
        stopMessagePolling(orderId);
      }
    };
  }, [orderId, isAr]);

  useEffect(() => {
    // Scroll message container to bottom
    const timer = setTimeout(() => {
      if (messageStreamRef.current) {
        messageStreamRef.current.scrollTop = messageStreamRef.current.scrollHeight;
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [orderMessages]);

  if (!orderId) {
    return (
      <div className="container py-4 py-lg-5" dir={isAr ? "rtl" : "ltr"}>
        <div className="alert alert-danger">{isAr ? "معرف الطلب غير صالح." : "Invalid order ID."}</div>
        <Link to="/my-service-orders" className="btn btn-primary">
          {isAr ? "العودة للطلبات" : "Back to Orders"}
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary mb-3" role="status" />
        <p>{isAr ? "جارِ التحميل..." : "Loading..."}</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container py-4 py-lg-5" dir={isAr ? "rtl" : "ltr"}>
        <div className="alert alert-danger">{error || (isAr ? "الطلب غير موجود." : "Order not found.")}</div>
        <Link to="/my-service-orders" className="btn btn-primary">
          {isAr ? "العودة للطلبات" : "Back to Orders"}
        </Link>
      </div>
    );
  }

  const service = order.service || {};
  const statusMeta = getStatusMeta(order.status, isAr);
  const review = order.review || null;
  const disputes = Array.isArray(order.disputes) ? order.disputes : [];
  const milestones = Array.isArray(order.milestones) ? order.milestones : [];
  const refunds = Array.isArray(order.refunds) ? order.refunds : [];

  const handleStatusUpdate = async (status) => {
    try {
      setActionInProgress(true);
      setError("");
      setNotice("");

      const result = await updateServiceOrderStatus(orderId, { status });
      const nextOrder = result?.data || null;

      if (nextOrder) {
        setOrder((current) => ({
          ...current,
          ...nextOrder,
          milestones: Array.isArray(nextOrder.milestones) ? nextOrder.milestones : current?.milestones || [],
          disputes: Array.isArray(nextOrder.disputes) ? nextOrder.disputes : current?.disputes || [],
          refunds: Array.isArray(nextOrder.refunds) ? nextOrder.refunds : current?.refunds || [],
          review: nextOrder.review ?? current?.review ?? null,
          payment_summary: nextOrder.payment_summary ?? current?.payment_summary ?? null,
        }));
      }

      setNotice(
        result?.message ||
          (isAr ? "تم تحديث حالة الطلب بنجاح." : "Service order status updated successfully.")
      );

      // Trigger navbar to refresh notifications
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Service order status update error", err);
      setError(err?.message || (isAr ? "تعذر تحديث الحالة." : "Unable to update the status."));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleSubmitReview = async () => {
    try {
      setActionInProgress(true);
      setError("");
      setNotice("");

      const reviewData = await submitServiceOrderReview(orderId, {
        rating: Number(reviewDraft.rating || 5),
        title: reviewDraft.title.trim() || undefined,
        comment: reviewDraft.comment.trim() || undefined,
      });

      setOrder((current) => ({
        ...current,
        review: reviewData,
      }));
      setReviewDraft(createReviewDraft());
      setNotice(isAr ? "تم إرسال التقييم بنجاح." : "Review submitted successfully.");

      // Trigger navbar to refresh notifications
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Submit service review error", err);
      setError(err?.message || (isAr ? "تعذر إرسال التقييم." : "Unable to submit the review."));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleOpenDispute = async () => {
    const reason = disputeDraft.reason.trim();
    const details = disputeDraft.details.trim();
    const previousDisputes = Array.isArray(order.disputes) ? order.disputes : [];

    try {
      setActionInProgress(true);
      setError("");
      setNotice("");

      const dispute = await openServiceOrderDispute(orderId, {
        reason,
        details: details || undefined,
      });

      setOrder((current) => ({
        ...current,
        status: "disputed",
        disputes: [dispute, ...(Array.isArray(current.disputes) ? current.disputes : [])],
      }));
      setDisputeDraft(createDisputeDraft());
      setNotice(isAr ? "تم فتح النزاع بنجاح." : "Dispute opened successfully.");

      // Trigger navbar to refresh notifications
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Open service dispute error", err);
      try {
        const latestDisputes = await listServiceOrderDisputes(orderId);
        const previousIds = new Set(previousDisputes.map((dispute) => dispute?.id).filter(Boolean));
        const recoveredDispute = latestDisputes.find((dispute) => {
          const disputeReason = String(dispute?.reason || "").trim();
          return !previousIds.has(dispute?.id) && (!reason || disputeReason === reason);
        });

        if (recoveredDispute) {
          setOrder((current) => ({
            ...current,
            status: "disputed",
            disputes: latestDisputes,
          }));
          setDisputeDraft(createDisputeDraft());
          setNotice(isAr ? "تم فتح النزاع بنجاح. ستتم مراجعة الطلب من قبل الدعم." : "Dispute opened successfully. Support will review the order.");
          window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
          return;
        }
      } catch (refreshErr) {
        logger.error("Refresh disputes after open error failed", refreshErr);
      }

      setError(err?.message || (isAr ? "تعذر فتح النزاع." : "Unable to open the dispute."));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleApproveMilestone = async (milestoneId) => {
    try {
      setActionInProgress(true);
      setError("");
      setNotice("");

      const response = await approveServiceOrderMilestone(milestoneId);
      const nextMilestone = response?.milestone || response?.data?.milestone || null;
      const nextPaymentSummary = response?.payment_summary || response?.data?.payment_summary || null;

      if (nextMilestone) {
        setOrder((current) => ({
          ...current,
          milestones: (Array.isArray(current.milestones) ? current.milestones : []).map((milestone) =>
            milestone.id === milestoneId ? nextMilestone : milestone,
          ),
          payment_summary: nextPaymentSummary || current.payment_summary,
        }));
      }

      setNotice(isAr ? "تمت الموافقة على المعلم بنجاح." : "Milestone approved successfully.");

      // Trigger navbar to refresh notifications
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Approve service milestone error", err);
      setError(err?.message || (isAr ? "تعذر اعتماد المعلم." : "Unable to approve the milestone."));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleSendMessage = async () => {
    // Prevent duplicate submissions
    if (actionInProgress) return;

    const text = messageText.trim();
    if (!text && !messageFile) return;
    const previousMessages = Array.isArray(orderMessages) ? orderMessages : [];
    const hadAttachment = Boolean(messageFile);

    try {
      setActionInProgress(true);
      setError("");

      const formData = new FormData();
      formData.append("message", text);
      if (messageFile) {
        formData.append("attachment", messageFile);
      }

      const newMessage = await sendOrderMessage(orderId, formData);
      setOrderMessages([...orderMessages, newMessage]);
      setMessageText("");
      setMessageFile(null);

      // Trigger navbar to refresh notifications
      refreshNotifications();
    } catch (err) {
      logger.error("Send message error", err);
      try {
        const latestMessages = await getOrderMessages(orderId);
        const recoveredMessage = findRecoveredOrderMessage(previousMessages, latestMessages, text, hadAttachment);

        if (recoveredMessage) {
          setOrderMessages(latestMessages);
          setMessageText("");
          setMessageFile(null);
          window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
          return;
        }
      } catch (refreshErr) {
        logger.error("Refresh messages after send error failed", refreshErr);
      }
      setError(err?.message || (isAr ? "تعذر إرسال الرسالة." : "Unable to send message."));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleRequestCancellation = async () => {
    const reason = cancellationReason.trim();
    if (!reason) return;

    try {
      setActionInProgress(true);
      setError("");
      setNotice("");

      const result = await requestOrderCancellation(orderId, { reason });
      const nextOrder = result?.data || result || null;

      if (nextOrder) {
        setOrder((current) => ({
          ...current,
          ...nextOrder,
          status: nextOrder.status || current.status,
        }));
      }

      setShowCancelForm(false);
      setCancellationReason("");
      setNotice(
        result?.message ||
          (isAr ? "تم إرسال طلب الإلغاء." : "Cancellation request submitted.")
      );

      // Trigger navbar to refresh notifications
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Request cancellation error", err);
      setError(err?.message || (isAr ? "تعذر طلب الإلغاء." : "Unable to request cancellation."));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleAcceptDelivery = async () => {
    try {
      setActionInProgress(true);
      setError("");
      setNotice("");

      const result = await acceptRejectDelivery(orderId, "accept");
      const nextOrder = result?.data || result || null;

      if (nextOrder) {
        setOrder((current) => ({
          ...current,
          ...nextOrder,
          status: nextOrder.status || current.status,
        }));
      }

      setNotice(result?.message || (isAr ? "تم قبول الخدمة." : "Service accepted."));
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Accept delivery error", err);
      setError(err?.message || (isAr ? "تعذر قبول الخدمة." : "Unable to accept service."));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleRejectDelivery = async () => {
    const reason = rejectionReason.trim();
    if (!reason) return;

    try {
      setActionInProgress(true);
      setError("");
      setNotice("");

      const result = await acceptRejectDelivery(orderId, "reject", reason);
      const nextOrder = result?.data || result || null;

      if (nextOrder) {
        setOrder((current) => ({
          ...current,
          ...nextOrder,
          status: nextOrder.status || current.status,
        }));
      }

      setShowRejectForm(false);
      setRejectionReason("");
      setNotice(result?.message || (isAr ? "تم طلب المراجعة." : "Revision requested."));
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Reject delivery error", err);
      setError(err?.message || (isAr ? "تعذر رفض الخدمة." : "Unable to reject service."));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleRequestRevision = async () => {
    try {
      setActionInProgress(true);
      setError("");
      setNotice("");

      const result = await updateServiceOrderStatus(orderId, { status: "revision_requested" });
      const nextOrder = result?.data || null;

      if (nextOrder) {
        setOrder((current) => ({
          ...current,
          ...nextOrder,
          milestones: Array.isArray(nextOrder.milestones) ? nextOrder.milestones : current?.milestones || [],
          disputes: Array.isArray(nextOrder.disputes) ? nextOrder.disputes : current?.disputes || [],
          refunds: Array.isArray(nextOrder.refunds) ? nextOrder.refunds : current?.refunds || [],
          review: nextOrder.review ?? current?.review ?? null,
          payment_summary: nextOrder.payment_summary ?? current?.payment_summary ?? null,
        }));
      }

      setNotice(isAr ? "تم طلب المراجعة بنجاح." : "Revision requested successfully.");
    } catch (err) {
      logger.error("Request revision error", err);
      setError(err?.message || (isAr ? "تعذر طلب المراجعة." : "Unable to request revision."));
    } finally {
      setActionInProgress(false);
    }
  };

  return (
    <div className="container py-4 py-lg-5 order-details-page" dir={isAr ? "rtl" : "ltr"}>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h1 className="h2 fw-bold mb-1">{isAr ? "تفاصيل الطلب" : "Order Details"}</h1>
          <p className="text-muted mb-0">
            {order.title || getFreelancerServiceTitle(service, isAr)}
          </p>
        </div>
        <Link to="/my-service-orders" className="btn btn-outline-dark rounded-3 px-4 order-details-back-btn">
          <i className={`bi ${isAr ? "bi-arrow-right" : "bi-arrow-left"} me-2`} />
          {isAr ? "العودة للطلبات" : "Back to Orders"}
        </Link>
      </div>

      {notice ? <div className="alert alert-success rounded-4 mb-4" role="alert" aria-live="polite">{notice}</div> : null}
      {error ? <div className="alert alert-danger rounded-4 mb-4" role="alert" aria-live="polite">{error}</div> : null}

      <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4 order-details-hero-card">
        <div className="card-body p-4 order-details-hero-card__content">
          <div className="order-details-hero-card__info d-flex flex-wrap justify-content-between gap-3 mb-4">
            <div className="d-flex gap-3 order-details-hero-card__left">
              <img
                src={
                  service?.cover_image ||
                  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80"
                }
                alt={getFreelancerServiceTitle(service, isAr)}
                className="course-my-card__image order-details-hero-image"
                loading="lazy"
              />
              <div>
                <div className="d-flex flex-wrap gap-2 mb-3 order-details-hero-badges">
                  <span className={`badge ${statusMeta.className}`}>{statusMeta.label}</span>
                  <span
                    className={`badge ${
                      order.payment_status === "paid"
                        ? "text-bg-success"
                        : order.payment_status === "refunded"
                          ? "text-bg-danger"
                          : "text-bg-warning"
                    }`}
                  >
                    {order.payment_status}
                  </span>
                  {order.payment_summary?.escrow_status ? (
                    <span className="badge text-bg-light">
                      {isAr ? "الضمان" : "Escrow"}: {order.payment_summary.escrow_status}
                    </span>
                  ) : null}
                </div>

                <h2 className="h3 fw-bold mb-2 order-details-hero-title">
                  {order.title || getFreelancerServiceTitle(service, isAr)}
                </h2>
                <div className="small text-muted d-grid gap-2 order-details-hero-stats">
                  <div>
                    {isAr ? "السعر:" : "Price:"}{" "}
                    {formatServiceCurrency(order.price || 0, order.currency || "USD")}
                  </div>
                  <div>
                    {isAr ? "المستقل:" : "Freelancer:"} {order.freelancer?.name || "-"}
                  </div>
                  {order.payment_summary ? (
                    <div>
                      {isAr ? "المسترد:" : "Refunded:"}{" "}
                      {formatServiceCurrency(
                        order.payment_summary.refund_total || 0,
                        order.payment_summary.currency || order.currency || "USD"
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="order-details-hero-card__actions d-flex flex-column align-items-start align-items-lg-end gap-3">
              {order.status === "delivered" ? (
                <>
                  <button
                    type="button"
                    className="btn order-details-btn order-details-btn--primary rounded-pill px-4"
                    disabled={actionInProgress}
                    onClick={() => handleAcceptDelivery()}
                  >
                    {actionInProgress
                      ? isAr
                        ? "جارِ القبول..."
                        : "Accepting..."
                      : isAr
                        ? "قبول الخدمة"
                        : "Accept Service"}
                  </button>
                  <button
                    type="button"
                    className="btn order-details-btn order-details-btn--danger rounded-pill px-4"
                    disabled={actionInProgress}
                    onClick={() => setShowRejectForm(!showRejectForm)}
                  >
                    {isAr ? "رفض الخدمة" : "Reject Service"}
                  </button>
                </>
              ) : null}
              {order.status === "paid" || order.status === "in_progress" ? (
                <button
                  type="button"
                  className="btn order-details-btn order-details-btn--danger rounded-pill px-4"
                  disabled={actionInProgress}
                  onClick={() => setShowCancelForm(!showCancelForm)}
                >
                  {isAr ? "إلغاء الطلب" : "Cancel Order"}
                </button>
              ) : null}
            </div>
          </div>

          {showCancelForm ? (
            <div className="alert alert-warning border rounded-4 mb-4">
              <strong>{isAr ? "تأكيد إلغاء الطلب" : "Confirm Cancellation"}</strong>
              <textarea
                rows="2"
                className="form-control mt-2 mb-2"
                placeholder={isAr ? "سبب الإلغاء (مطلوب)" : "Reason for cancellation (required)"}
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                aria-label={isAr ? "سبب إلغاء الطلب" : "Reason for cancellation"}
              />
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-danger btn-sm rounded-pill"
                  disabled={actionInProgress || !cancellationReason.trim()}
                  onClick={() => handleRequestCancellation()}
                >
                  {actionInProgress
                    ? isAr
                      ? "جارِ الإرسال..."
                      : "Submitting..."
                    : isAr
                      ? "تأكيد الإلغاء"
                      : "Confirm Cancellation"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm rounded-pill"
                  onClick={() => setShowCancelForm(false)}
                >
                  {isAr ? "إلغاء" : "Cancel"}
                </button>
              </div>
            </div>
          ) : null}

          {showRejectForm ? (
            <div className="alert alert-danger border rounded-4 mb-4">
              <strong>{isAr ? "رفض الخدمة" : "Reject Service"}</strong>
              <textarea
                rows="2"
                className="form-control mt-2 mb-2"
                placeholder={isAr ? "سبب الرفض (مطلوب)" : "Reason for rejection (required)"}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                aria-label={isAr ? "سبب رفض الخدمة" : "Reason for rejection"}
              />
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-danger btn-sm rounded-pill"
                  disabled={actionInProgress || !rejectionReason.trim()}
                  onClick={() => handleRejectDelivery()}
                >
                  {actionInProgress
                    ? isAr
                      ? "جارِ الإرسال..."
                      : "Submitting..."
                    : isAr
                      ? "تأكيد الرفض"
                      : "Confirm Rejection"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm rounded-pill"
                  onClick={() => setShowRejectForm(false)}
                >
                  {isAr ? "إلغاء" : "Cancel"}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {order.description ? (
        <div className="alert alert-light border rounded-4 mb-4">
          <strong>{isAr ? "ملخص الطلب:" : "Order brief:"}</strong> {order.description}
        </div>
      ) : null}

      {Array.isArray(order.delivery_files) && order.delivery_files.length > 0 ? (
        <div className="border rounded-4 p-4 mb-4">
          <h3 className="h4 fw-bold mb-3">{isAr ? "الملفات المسلمة" : "Delivery Files"}</h3>
          <div className="d-grid gap-2">
            {order.delivery_files.map((file) => (
              <div key={file.id} className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3 border rounded-3 bg-light">
                <div>
                  <div className="small text-muted">{file.file_name}</div>
                  {file.delivery_notes ? <div className="small mt-1">{file.delivery_notes}</div> : null}
                </div>
                <a
                  href={`${getStorageBaseUrl()}/storage/service-deliveries/${order.id}/${file.file_name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-primary rounded-pill"
                >
                  {isAr ? "تحميل" : "Download"}
                </a>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="order-details-dashboard-grid mb-4">
        <div className="col-12 col-lg-6">
          <div className="order-details-card p-4 h-100">
            <div className="order-details-card__header d-flex align-items-center justify-content-between mb-4 gap-2">
              <h3 className="h5 fw-bold mb-0">{isAr ? "الدفعة والضمان" : "Payment & Escrow"}</h3>
              <span className="order-details-chip">{order.payment_summary?.escrow_status || (isAr ? "غير متوفر" : "No escrow")}</span>
            </div>
            <div className="small text-muted d-grid gap-3 order-details-card__list">
              <div>
                {isAr ? "إجمالي الدفع:" : "Total paid:"}{" "}
                {formatServiceCurrency(
                  order.payment_summary?.total_amount || order.price || 0,
                  order.payment_summary?.currency || order.currency || "USD"
                )}
              </div>
              <div>
                {isAr ? "العمولة:" : "Platform fee:"}{" "}
                {formatServiceCurrency(
                  order.payment_summary?.platform_fee || 0,
                  order.payment_summary?.currency || order.currency || "USD"
                )}
              </div>
              <div>
                {isAr ? "الضمان المحرر:" : "Released from escrow:"}{" "}
                {formatServiceCurrency(
                  order.payment_summary?.released_amount || 0,
                  order.payment_summary?.currency || order.currency || "USD"
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="order-details-card p-4 h-100">
            <div className="order-details-card__header d-flex align-items-center justify-content-between mb-4 gap-2">
              <h3 className="h5 fw-bold mb-0">{isAr ? "المعالم" : "Milestones"}</h3>
              <span className="order-details-chip order-details-chip--info">
                {milestones.length > 0 ? `${milestones.length} ${isAr ? "معلم" : "Milestones"}` : isAr ? "بدون معالم" : "No milestones"}
              </span>
            </div>
            {milestones.length === 0 ? (
              <div className="empty-milestones-container text-center text-muted">
                <i className="bi bi-clipboard-check empty-milestone-icon" />
                <p className="empty-milestone-text mb-0">
                  {isAr ? "لا توجد معالم لهذا الطلب بعد." : "No milestones were added to this order yet."}
                </p>
              </div>
            ) : (
              <div className="d-grid gap-3 order-details-card__list">
                {milestones.map((milestone) => (
                  <div key={milestone.id} className="order-details-card order-details-card--item p-3">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                      <strong>{milestone.title}</strong>
                      <span className="badge text-bg-light">{milestone.status}</span>
                    </div>
                    <div className="small text-muted">
                      {formatServiceCurrency(milestone.amount || 0, milestone.currency || order.currency || "USD")}
                    </div>
                    {milestone.description ? <div className="small mt-2">{milestone.description}</div> : null}
                    {milestone.status === "pending" ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success rounded-pill mt-3"
                        disabled={actionInProgress}
                        onClick={() => handleApproveMilestone(milestone.id)}
                      >
                        {actionInProgress
                          ? isAr
                            ? "جارِ الاعتماد..."
                            : "Approving..."
                          : isAr
                            ? "اعتماد المعلم"
                            : "Approve Milestone"}
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="order-details-card p-4 h-100">
            <div className="order-details-card__header d-flex align-items-center justify-content-between mb-4 gap-2">
              <h3 className="h5 fw-bold mb-0">{isAr ? "النزاعات" : "Disputes"}</h3>
              <span className="order-details-chip order-details-chip--warning">
                {disputes.length > 0 ? `${disputes.length} ${isAr ? "نزاع" : "Disputes"}` : isAr ? "لا توجد نزاعات" : "No disputes"}
              </span>
            </div>
            <div className="alert alert-warning py-2 small mb-3">
              {isAr
                ? "بعد فتح النزاع يتم إيقاف إجراءات الطلب مؤقتًا، ثم يراجع فريق الدعم التفاصيل والرسائل والمرفقات قبل تحديث الحالة أو التواصل مع الطرفين."
                : "After a dispute is opened, order actions are paused while support reviews the details, messages, and attachments before updating the status or contacting both parties."}
            </div>
            {disputes.length > 0 ? (
              <div className="d-grid gap-3 mb-3 order-details-card__list">
                {disputes.map((dispute) => (
                  <div key={dispute.id} className="border rounded-4 p-3">
                    <div className="d-flex flex-wrap justify-content-between gap-2 mb-1">
                      <strong>{dispute.reason}</strong>
                      <span className="badge text-bg-light">{dispute.status}</span>
                    </div>
                    <div className="small text-muted">{dispute.details || (isAr ? "لا توجد تفاصيل إضافية." : "No extra details.")}</div>
                  </div>
                ))}
              </div>
            ) : null}

            {order.status !== "disputed" && disputes.length === 0 ? (
              <>
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder={isAr ? "سبب النزاع" : "Dispute reason"}
                  value={disputeDraft.reason}
                  onChange={(e) => setDisputeDraft({ ...disputeDraft, reason: e.target.value })}
                  aria-label={isAr ? "سبب النزاع" : "Dispute reason"}
                />
                <textarea
                  rows="3"
                  className="form-control mb-3"
                  placeholder={isAr ? "تفاصيل إضافية" : "Additional details"}
                  value={disputeDraft.details}
                  onChange={(e) => setDisputeDraft({ ...disputeDraft, details: e.target.value })}
                  aria-label={isAr ? "تفاصيل النزاع الإضافية" : "Additional dispute details"}
                />
                <button
                  type="button"
                  className="btn btn-outline-danger rounded-pill"
                  disabled={actionInProgress || !disputeDraft.reason.trim()}
                  onClick={() => handleOpenDispute()}
                >
                  {actionInProgress
                    ? isAr
                      ? "جارِ الإرسال..."
                      : "Submitting..."
                    : isAr
                      ? "فتح نزاع"
                      : "Open Dispute"}
                </button>
              </>
            ) : (
              <div className="text-muted small">
                {isAr ? "يوجد نزاع مفتوح أو سابق على هذا الطلب." : "This order already has an active or previous dispute."}
              </div>
            )}
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="order-details-card p-4 h-100">
            <div className="order-details-card__header d-flex align-items-center justify-content-between mb-4 gap-2">
              <h3 className="h5 fw-bold mb-0">{isAr ? "التقييم والاسترداد" : "Review & Refunds"}</h3>
              <span className="order-details-chip order-details-chip--success">
                {refunds.length > 0 ? `${refunds.length} ${isAr ? "استرداد" : "Refunds"}` : isAr ? "بدون استردادات" : "No refunds"}
              </span>
            </div>
            {refunds.length > 0 ? (
              <div className="d-grid gap-2 mb-3">
                {refunds.map((refund) => (
                  <div key={refund.id} className="border rounded-4 p-3">
                    <div className="d-flex flex-wrap justify-content-between gap-2 mb-1">
                      <strong>
                        {formatServiceCurrency(refund.amount || 0, refund.currency || order.currency || "USD")}
                      </strong>
                      <span className="badge text-bg-light">{refund.status}</span>
                    </div>
                    <div className="small text-muted">{refund.reason || (isAr ? "بدون سبب مسجل." : "No reason recorded.")}</div>
                  </div>
                ))}
              </div>
            ) : null}

            {review ? (
              <div className="border rounded-4 p-3">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                  <strong>{review.title || (isAr ? "تقييمك" : "Your review")}</strong>
                  <StarRating value={review.rating || 0} size={16} showValue />
                </div>
                <div className="text-muted">{review.comment || (isAr ? "بدون تعليق إضافي." : "No additional comment.")}</div>
              </div>
            ) : order.status === "completed" ? (
              <>
                <div className="row g-2 mb-2">
                  <div className="col-sm-4">
                    <select
                      className="form-select"
                      value={reviewDraft.rating}
                      onChange={(e) => setReviewDraft({ ...reviewDraft, rating: e.target.value })}
                    >
                      {[5, 4, 3, 2, 1].map((rating) => (
                        <option key={rating} value={rating}>
                          {rating}/5
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      className="form-control"
                      placeholder={isAr ? "عنوان التقييم" : "Review title"}
                      value={reviewDraft.title}
                      onChange={(e) => setReviewDraft({ ...reviewDraft, title: e.target.value })}
                    />
                  </div>
                </div>
                <textarea
                  rows="3"
                  className="form-control mb-3"
                  placeholder={isAr ? "اكتب انطباعك عن الخدمة" : "Share your feedback about the service"}
                  value={reviewDraft.comment}
                  onChange={(e) => setReviewDraft({ ...reviewDraft, comment: e.target.value })}
                />
                <button
                  type="button"
                  className="btn btn-primary rounded-pill"
                  disabled={actionInProgress}
                  onClick={() => handleSubmitReview()}
                >
                  {actionInProgress
                    ? isAr
                      ? "جارِ الإرسال..."
                      : "Submitting..."
                    : isAr
                      ? "إرسال التقييم"
                      : "Submit Review"}
                </button>
              </>
            ) : (
              <div className="text-muted small">
                {isAr ? "يمكنك إضافة تقييم بعد اكتمال الطلب." : "You can add a review after the order is completed."}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="order-details-card p-4 order-details-messages-card">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
          <h3 className="h4 fw-bold mb-0">{isAr ? "الرسائل" : "Messages"}</h3>
          <span className="order-details-chip order-details-chip--blue">
            {Array.isArray(orderMessages) ? orderMessages.length : 0} {isAr ? "رسائل" : "Messages"}
          </span>
        </div>
        <div className="order-details-message-stream mb-4" ref={messageStreamRef}>
          {Array.isArray(orderMessages) && orderMessages.length > 0 ? (
            orderMessages.map((msg) => (
              <div
                key={msg.id}
                className={`order-details-message ${msg.sender_id === order.client?.id ? "order-details-message--outgoing" : "order-details-message--incoming"}`}
              >
                <div className="order-details-message__meta">
                  <span>{msg.sender?.name || (msg.sender_id === order.client?.id ? order.client?.name : "Freelancer")}</span>
                  {msg.created_at ? <span>{new Date(msg.created_at).toLocaleDateString(isAr ? "ar" : "en")}</span> : null}
                </div>
                <div className="order-details-message__bubble">
                  {msg.message}
                  {msg.attachment_path ? (
                    <div className="order-details-message__attachment">
                      <a
                        href={`${getStorageBaseUrl()}/storage/service-messages/${order.id}/${msg.attachment_name}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {msg.attachment_name}
                      </a>
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          ) : (
            <div className="empty-messages-container">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="empty-chat-icon">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a.596.596 0 0 1-.733-.525 5.961 5.961 0 0 1 .716-4.485C4.203 14.55 3 13.374 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
              </svg>
              <p className="empty-chat-text">{isAr ? "لا توجد رسائل بعد" : "No messages yet"}</p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="order-details-message-composer">
          <textarea
            rows="3"
            className="form-control order-details-message-input"
            placeholder={isAr ? "اكتب رسالتك..." : "Type your message..."}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            aria-label={isAr ? "رسالة الطلب" : "Order message"}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && messageText.trim()) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
          />
          <div className="order-details-message-actions d-flex flex-column flex-sm-row align-items-center gap-2 mt-3">
            <label htmlFor="order-file-upload" className="btn btn-outline-secondary rounded-pill order-details-file-btn">
              {isAr ? "إرفاق ملف" : "Attach file"}
            </label>
            <input
              id="order-file-upload"
              type="file"
              accept={SERVICE_ORDER_ATTACHMENT_ACCEPT}
              className="d-none"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                if (!file) {
                  setMessageFile(null);
                  setMessageFileError("");
                  return;
                }
                const validationError = validateServiceOrderAttachment(file);
                if (validationError) {
                  setMessageFileError(validationError);
                  setMessageFile(null);
                  e.target.value = "";
                  return;
                }
                setMessageFileError("");
                setMessageFile(file);
              }}
            />
            {messageFileError ? (
              <div className="small text-danger order-details-file-error">
                {messageFileError}
              </div>
            ) : messageFile ? (
              <div className="small text-muted order-details-file-name">
                {messageFile.name}
              </div>
            ) : null}
            <button
              type="button"
              className="btn order-details-btn order-details-btn--primary rounded-pill px-4 ms-auto"
              disabled={actionInProgress || Boolean(messageFileError) || (!messageText.trim() && !messageFile)}
              onClick={() => handleSendMessage()}
            >
              {actionInProgress
                ? isAr
                  ? "جارِ الإرسال..."
                  : "Sending..."
                : isAr
                  ? "إرسال"
                  : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
